import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiBarChart2, FiPieChart, FiTag, FiAlertCircle, FiClock } = FiIcons;

const MetadataAnalysis = ({ questions }) => {
  const [subjectDistribution, setSubjectDistribution] = useState({});
  const [typeDistribution, setTypeDistribution] = useState({});
  const [difficultyDistribution, setDifficultyDistribution] = useState({
    'Easy': 0,
    'Medium': 0,
    'Hard': 0,
    'Very Hard': 0,
    'Unknown': 0
  });
  const [markDistribution, setMarkDistribution] = useState({});
  const [averageStats, setAverageStats] = useState({
    avgMarks: 0,
    avgWordCount: 0,
    totalQuestions: 0
  });

  useEffect(() => {
    if (!questions.length) return;
    
    // Process subject distribution
    const subjects = {};
    questions.forEach(q => {
      const subject = q.subject || 'Uncategorized';
      subjects[subject] = (subjects[subject] || 0) + 1;
    });
    setSubjectDistribution(subjects);
    
    // Process question type distribution
    const types = {};
    questions.forEach(q => {
      const type = q.type || 'Uncategorized';
      types[type] = (types[type] || 0) + 1;
    });
    setTypeDistribution(types);
    
    // Process difficulty distribution
    const difficulties = {
      'Easy': 0,
      'Medium': 0,
      'Hard': 0,
      'Very Hard': 0,
      'Unknown': 0
    };
    questions.forEach(q => {
      const difficulty = q.difficulty || 'Unknown';
      difficulties[difficulty] = (difficulties[difficulty] || 0) + 1;
    });
    setDifficultyDistribution(difficulties);
    
    // Process mark distribution
    const marks = {};
    questions.forEach(q => {
      const mark = q.marks || 0;
      marks[mark] = (marks[mark] || 0) + 1;
    });
    setMarkDistribution(marks);
    
    // Calculate average stats
    const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0);
    const totalWords = questions.reduce((sum, q) => sum + countWords(q.text), 0);
    
    setAverageStats({
      avgMarks: (totalMarks / questions.length).toFixed(1),
      avgWordCount: Math.round(totalWords / questions.length),
      totalQuestions: questions.length
    });
    
  }, [questions]);
  
  // Helper function to count words in text
  const countWords = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter(word => word.length > 0).length;
  };
  
  // Prepare chart options for ECharts
  const getPieChartOptions = (title, data) => {
    return {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 14
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        type: 'scroll',
        formatter: (name) => {
          // Truncate long names
          return name.length > 15 ? name.substring(0, 12) + '...' : name;
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: Object.entries(data).map(([name, value]) => ({ name, value }))
        }
      ]
    };
  };
  
  // Prepare bar chart for mark distribution
  const getMarkDistributionOptions = () => {
    const marks = Object.keys(markDistribution).sort((a, b) => parseInt(a) - parseInt(b));
    const counts = marks.map(mark => markDistribution[mark]);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: marks,
        axisTick: {
          alignWithLabel: true
        },
        name: 'Marks',
        nameLocation: 'middle',
        nameGap: 25
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        nameLocation: 'middle',
        nameGap: 30
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          barWidth: '60%',
          data: counts,
          itemStyle: {
            color: '#3b82f6'
          }
        }
      ]
    };
  };
  
  // Prepare difficulty distribution chart
  const getDifficultyDistributionOptions = () => {
    const difficulties = Object.keys(difficultyDistribution).filter(d => d !== 'Unknown');
    const counts = difficulties.map(d => difficultyDistribution[d]);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: difficulties,
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          barWidth: '60%',
          data: counts,
          itemStyle: {
            color: function(params) {
              const colors = ['#10b981', '#fbbf24', '#f97316', '#ef4444'];
              return colors[params.dataIndex];
            }
          }
        }
      ]
    };
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiBarChart2} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Basic Metadata Analysis</h3>
      </div>
      
      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-50 p-4 rounded-lg flex items-center">
          <div className="p-3 rounded-full bg-blue-100 mr-4">
            <SafeIcon icon={FiPieChart} className="text-blue-600 text-xl" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Total Questions</div>
            <div className="text-xl font-semibold">{averageStats.totalQuestions}</div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg flex items-center">
          <div className="p-3 rounded-full bg-green-100 mr-4">
            <SafeIcon icon={FiTag} className="text-green-600 text-xl" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Average Marks</div>
            <div className="text-xl font-semibold">{averageStats.avgMarks}</div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg flex items-center">
          <div className="p-3 rounded-full bg-yellow-100 mr-4">
            <SafeIcon icon={FiClock} className="text-yellow-600 text-xl" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Average Word Count</div>
            <div className="text-xl font-semibold">{averageStats.avgWordCount}</div>
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="h-72">
          <ReactECharts 
            option={getPieChartOptions('Subject Distribution', subjectDistribution)} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
        
        <div className="h-72">
          <ReactECharts 
            option={getPieChartOptions('Question Type Distribution', typeDistribution)} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="h-72">
          <ReactECharts 
            option={getDifficultyDistributionOptions()} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
        
        <div className="h-72">
          <ReactECharts 
            option={getMarkDistributionOptions()} 
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
      
      {/* Recommendations */}
      {Object.keys(difficultyDistribution).includes('Unknown') && difficultyDistribution['Unknown'] > 0 && (
        <div className="mt-8 bg-yellow-50 p-4 rounded-lg flex items-start">
          <SafeIcon icon={FiAlertCircle} className="text-yellow-500 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Missing Difficulty Levels</h4>
            <p className="text-sm text-gray-600">
              {difficultyDistribution['Unknown']} questions ({Math.round((difficultyDistribution['Unknown'] / averageStats.totalQuestions) * 100)}%) are missing difficulty levels. 
              Adding difficulty metadata will improve assessment balance and question selection.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataAnalysis;